/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  archiveDatabase,
  bulkUploadProducts,
  bulkUploadSuppliers,
  bulkUploadTariffs,
  createDatabase,
  deleteDatabase,
  filterDatabases,
  getDatabaseById,
  getDatabaseListItemsByGroupId,
  getDatabasesByGroupId,
  getDatabasesByGroupIdPagination,
  restoreDatabase,
  updateDatabase,
} from './database.service';
import { hasKeyPrefix, Time } from '../shared';
import { STATS_QUERY_KEYS } from '../stats/stats.hooks';
import { auditKeys } from '../audit/audit.hooks';
import { entityKeys } from '../entities/entities.keys';
import { DatabasePaginationParams, IDatabase } from './database';

// Query key constants
const QUERY_KEYS = {
  DATABASE: 'database',
  DATABASES: 'databases',
  BY_GROUP: 'by-group',
  BY_GROUP_LIST_ITEMS: 'by-group-list-items',
  BY_ID: 'by-id',
} as const;

export const databaseKeys = {
  all: () => [QUERY_KEYS.DATABASE] as const,
  databases: () => [...databaseKeys.all(), QUERY_KEYS.DATABASES] as const,
  databasesByGroup: (groupId: string) =>
    [...databaseKeys.databases(), QUERY_KEYS.BY_GROUP, groupId] as const,
  databasesListItemsByGroup: (groupId: string, searchType?: string) =>
    [
      ...databaseKeys.databases(),
      QUERY_KEYS.BY_GROUP_LIST_ITEMS,
      groupId,
      searchType,
    ] as const,
  databasesByGroupPagination: (
    groupId: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
      subtype?: string;
      type?: string;
      database_structure?: string;
      my_dbs?: string | undefined | null;
    },
  ) =>
    [
      ...databaseKeys.databases(),
      QUERY_KEYS.BY_GROUP,
      groupId,
      {
        page: options?.page || 1,
        limit: options?.limit || 10,
        sortBy: options?.sortBy || 'created_at',
        sortOrder: options?.sortOrder || 'desc',
        search: options?.search || '',
        subtype: options?.subtype || '',
        type: options?.type || '',
        group_id: groupId || '',
        database_structure: options?.database_structure || '',
      },
    ] as const,
  database: () => [...databaseKeys.all(), QUERY_KEYS.BY_ID] as const,
  databaseById: (id: string) => [...databaseKeys.database(), id] as const,
};

export interface DatabasesByGroupOptions {
  /**
   * Poll only while a database of this type is screening. The Entity Hub grid
   * passes 'people & companies' so a product or tariff run in the same team
   * doesn't keep refetching the heavy grid aggregate. Omitted: any type.
   */
  pollWhileScreeningType?: IDatabase['type'];
}

export const DATABASES_GRID_POLL_INTERVAL = 15 * Time.SECOND;

export const useGetDatabasesByGroupId = (
  groupId: string,
  searchTerm?: string,
  options: DatabasesByGroupOptions = {},
) => {
  const { pollWhileScreeningType } = options;
  const {
    data: allDatabases,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: databaseKeys.databasesByGroup(groupId),

    queryFn: () => getDatabasesByGroupId(groupId),
    staleTime: 12 * Time.HOUR,
    gcTime: Number(Time.HOUR),
    enabled: Boolean(groupId),
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (!Array.isArray(rows)) {
        return false;
      }
      const anyInProgress = rows.some(
        (database: IDatabase) =>
          database.last_screening_status === 'in-progress' &&
          (!pollWhileScreeningType || database.type === pollWhileScreeningType),
      );
      return anyInProgress ? DATABASES_GRID_POLL_INTERVAL : false;
    },
    // Never poll from a hidden browser tab (this is also the library default).
    refetchIntervalInBackground: false,
  });

  const filteredDatabases = useMemo(() => {
    if (!allDatabases) {
      return [];
    }
    return filterDatabases(allDatabases, searchTerm || '');
  }, [allDatabases, searchTerm]);

  return {
    data: filteredDatabases,
    allData: allDatabases,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

export const useGetDatabaseListItemsByGroupId = (
  groupId: string,
  searchType?: string,
) => {
  const {
    data: allDatabases,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: databaseKeys.databasesListItemsByGroup(groupId, searchType),

    queryFn: () => getDatabaseListItemsByGroupId(groupId, searchType),
    staleTime: 12 * Time.HOUR,
    gcTime: Number(Time.HOUR),
    enabled: Boolean(groupId),
  });

  return {
    allData: allDatabases,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

export const useGetDatabasesByGroupIdPagination = (
  groupId: string,
  searchTerm = '',
  searchOptions: DatabasePaginationParams,
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: databaseKeys.databasesByGroupPagination(groupId, {
      page: searchOptions.page,
      limit: searchOptions.limit,
      sortBy: searchOptions.sortBy,
      sortOrder: searchOptions.sortOrder,
      search: searchTerm,
      subtype: searchOptions.subtype,
      type: searchOptions.type,
      database_structure: searchOptions.database_structure,
      my_dbs: searchOptions.my_dbs,
    }),
    queryFn: () =>
      getDatabasesByGroupIdPagination(groupId, {
        ...searchOptions,
        search: searchTerm,
      }),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(groupId),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

export const useGetDatabaseById = (id?: string, shouldRun?: boolean) => {
  return useQuery({
    queryKey: databaseKeys.databaseById(id || ''),
    queryFn: () => getDatabaseById(id),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(id) && shouldRun,
  });
};

// Mutation hook for creating a database
export const useCreateDatabase = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: (database: any) => createDatabase(database),
    onSuccess: (data, variables) => {
      const createdDatabase = data?.result?.data as IDatabase | undefined;
      if (createdDatabase?.id) {
        queryClient.setQueryData<IDatabase[]>(
          databaseKeys.databasesByGroup(variables.group_id),
          (existing) => {
            const current = existing ? [...existing] : [];
            const alreadyExists = current.some(
              (db) => db.id === createdDatabase.id,
            );
            if (alreadyExists) {
              return current.map((db) =>
                db.id === createdDatabase.id
                  ? { ...db, ...createdDatabase }
                  : db,
              );
            }
            return [createdDatabase, ...current];
          },
        );
      }

      // Force immediate invalidation and refetch
      queryClient.invalidateQueries({
        queryKey: databaseKeys.databasesByGroup(variables.group_id),
        refetchType: 'active',
      });

      // Also invalidate all database keys as backup
      queryClient.invalidateQueries({
        queryKey: databaseKeys.all(),
        refetchType: 'active',
      });

      // Force refetch of the specific query
      queryClient.refetchQueries({
        queryKey: databaseKeys.databasesByGroup(variables.group_id),
      });

      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Use comprehensive invalidation
      invalidateAllDatabaseKeys(variables.group_id);

      // Invalidate audit logs for the new database (if we have the database ID from response)
      if (data?.result?.data?.id) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(data.result.data.id),
          exact: false,
        });
      }
    },
    onError: (error) => {
      console.error('Error creating database:', error);
    },
  });
};

// Mutation hook for bulk uploading suppliers
export const useBulkUploadSuppliers = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      formData,
    }: {
      formData: FormData;
      groupId?: string;
    }) => bulkUploadSuppliers(formData),
    onSuccess: (data, variables) => {
      // Use comprehensive invalidation for all database-related keys
      invalidateAllDatabaseKeys(variables.groupId);
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Invalidate all audit logs since bulk upload affects multiple entities
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error bulk uploading suppliers:', error);
    },
  });
};

export const useBulkUploadTariffs = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      formData,
    }: {
      formData: FormData;
      groupId?: string;
    }) => bulkUploadTariffs(formData),
    onSuccess: (data, variables) => {
      // Use comprehensive invalidation for all database-related keys
      invalidateAllDatabaseKeys(variables.groupId);
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Invalidate all audit logs since bulk upload affects multiple entities
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error bulk uploading tariff transactions:', error);
    },
  });
};

// Mutation hook for bulk uploading products
export const useBulkUploadProducts = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      formData,
    }: {
      formData: FormData;
      groupId?: string;
    }) => bulkUploadProducts(formData),
    onSuccess: (data, variables) => {
      // Use comprehensive invalidation for all database-related keys
      invalidateAllDatabaseKeys(variables.groupId);
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Invalidate all audit logs since bulk upload affects multiple products
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error bulk uploading products:', error);
    },
  });
};

// Mutation hook for updating a database
export const useUpdateDatabase = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      id,
      database,
    }: {
      id: string;
      database: Partial<IDatabase>;
      groupId?: string;
    }) => updateDatabase(id, database),
    onSuccess: (data, variables) => {
      // Invalidate database queries
      invalidateAllDatabaseKeys(variables.groupId);

      // Invalidate the specific database
      queryClient.invalidateQueries({
        queryKey: databaseKeys.databaseById(variables.id),
      });

      // Invalidate audit logs for this database
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByDatabase(variables.id),
        exact: false,
      });

      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error updating database:', error);
    },
  });
};

// Mutation hook for archiving a database
export const useArchiveDatabase = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({ id }: { id: string; groupId?: string }) =>
      archiveDatabase(id),
    onSuccess: (data, variables) => {
      // Invalidate database queries
      invalidateAllDatabaseKeys(variables.groupId);

      // Invalidate the specific database
      queryClient.invalidateQueries({
        queryKey: databaseKeys.databaseById(variables.id),
      });

      // Invalidate audit logs for this database
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByDatabase(variables.id),
        exact: false,
      });

      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error archiving database:', error);
    },
  });
};

// Mutation hook for restoring a database
export const useRestoreDatabase = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({ id }: { id: string; groupId?: string }) =>
      restoreDatabase(id),
    onSuccess: (data, variables) => {
      // Invalidate database queries
      invalidateAllDatabaseKeys(variables.groupId);

      // Invalidate the specific database
      queryClient.invalidateQueries({
        queryKey: databaseKeys.databaseById(variables.id),
      });

      // Invalidate audit logs for this database
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByDatabase(variables.id),
        exact: false,
      });

      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error restoring database:', error);
    },
  });
};

// Mutation hook for deleting a database
export const useDeleteDatabase = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({ id }: { id: string; groupId?: string }) =>
      deleteDatabase(id),
    onSuccess: (data, variables) => {
      invalidateAllDatabaseKeys(variables.groupId);
      queryClient.removeQueries({
        queryKey: databaseKeys.databaseById(variables.id),
      });
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error deleting database:', error);
    },
  });
};

// Utility function to invalidate all database-related keys, especially by group ID
export const useInvalidateAllDatabaseKeys = () => {
  const queryClient = useQueryClient();

  // One invalidation call: each overlapping call would cancel and resend the
  // fetch of every active query it matches (the grid and entity lists).
  const invalidateAllDatabaseKeys = (groupId?: string) => {
    const roots: ReadonlyArray<readonly unknown[]> = [
      databaseKeys.all(),
      entityKeys.all(),
      ['product'],
      ['tariff'],
      ['screening'],
      ['comment'],
      auditKeys.all(),
    ];

    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        if (!Array.isArray(queryKey)) {
          return false;
        }

        return (
          roots.some((root) => hasKeyPrefix(queryKey, root)) ||
          queryKey.includes('by-group') ||
          queryKey.includes('group') ||
          (groupId ? queryKey.includes(groupId) : false)
        );
      },
    });
  };

  return { invalidateAllDatabaseKeys: invalidateAllDatabaseKeys };
};
